/**
 * Active subscriptions by molecule: one ring per molecule, split by supply
 * length, plus an All ring first. A supply length keeps its color in every
 * ring (see .supply-viz in index.css). Hovering a segment or its row swaps
 * the ring's center to that length's count, share and monthly run rate.
 * Beside each active count is what that length is due to bill in the next
 * 30 days, the same window as the Billing next 30 days card.
 */

import { useState, useMemo } from 'react'
import { PieChart, Pie, Cell, Sector } from 'recharts'

const SUPPLY_SLOTS = new Set([1, 3, 4, 6, 7, 12, 13])
const colorOf = months => `var(--supply-${SUPPLY_SLOTS.has(months) ? months : 'other'})`

function pct(n, total) {
  if (!(total > 0)) return '–'
  const p = (100 * n) / total
  return n > 0 && p < 1 ? '<1%' : `${Math.round(p)}%`
}

function money(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((cents || 0) / 100)
}

/** The hovered segment, pushed out a few px. */
function ActiveSegment(props) {
  return <Sector {...props} outerRadius={props.outerRadius + 4} />
}

function Ring({ drug, share }) {
  const [active, setActive] = useState(null)
  const hovered = active != null ? drug.intervals[active] : null

  return (
    <div className="flex flex-col items-center rounded-lg border p-4">
      <div className="w-full flex items-baseline justify-between mb-1">
        <span className="font-semibold text-sm">{drug.drug}</span>
        <span className="text-xs text-muted-foreground">{share != null ? `${share} of active` : 'all molecules'}</span>
      </div>

      <div className="relative" style={{ width: 168, height: 168 }}>
        <PieChart width={168} height={168}>
          <Pie
            data={drug.intervals}
            dataKey="count"
            nameKey="interval"
            innerRadius={56}
            outerRadius={74}
            startAngle={90}
            endAngle={-270}
            stroke="hsl(var(--card))"
            strokeWidth={2}
            isAnimationActive={false}
            activeIndex={active ?? undefined}
            activeShape={ActiveSegment}
            onMouseEnter={(_, i) => setActive(i)}
            onMouseLeave={() => setActive(null)}
          >
            {drug.intervals.map(i => (
              <Cell key={i.interval} fill={colorOf(i.months)} cursor="pointer" />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
          {hovered ? (
            <>
              <span className="text-xs text-muted-foreground">{hovered.interval}</span>
              <span className="text-xl font-bold leading-tight">{hovered.count.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">{pct(hovered.count, drug.count)}</span>
              <span className="text-xs text-muted-foreground">{money(hovered.mrr)}</span>
              <span className="text-[11px] text-muted-foreground">a month</span>
            </>
          ) : (
            <>
              <span className="text-2xl font-bold leading-tight">{drug.count.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">active</span>
              <span className="text-xs text-muted-foreground">{money(drug.mrr)}</span>
              <span className="text-[11px] text-muted-foreground">a month</span>
            </>
          )}
        </div>
      </div>

      <div className="w-full mt-2 flex items-center gap-2 px-1.5 text-[11px] text-muted-foreground">
        <span className="w-2.5 shrink-0" />
        <span className="flex-1">Supply</span>
        <span className="w-10 text-right">Active</span>
        <span className="w-9 text-right">Share</span>
        <span className="w-[4.5rem] text-right whitespace-nowrap">Next 30 days</span>
      </div>
      <ul className="w-full space-y-0.5">
        {drug.intervals.map((i, idx) => (
          <li
            key={i.interval}
            onMouseEnter={() => setActive(idx)}
            onMouseLeave={() => setActive(null)}
            className={`flex items-center gap-2 text-xs rounded px-1.5 py-0.5 ${active === idx ? 'bg-muted' : ''}`}
          >
            <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: colorOf(i.months) }} />
            <span className="flex-1">{i.interval}</span>
            <span className="tabular-nums font-medium w-10 text-right">{i.count.toLocaleString()}</span>
            <span className="tabular-nums text-muted-foreground w-9 text-right">{pct(i.count, drug.count)}</span>
            <span className={`tabular-nums w-[4.5rem] text-right ${i.due ? 'font-medium' : 'text-muted-foreground'}`}>{i.due ? i.due.toLocaleString() : '–'}</span>
          </li>
        ))}
      </ul>
      <div className="w-full mt-2 pt-2 border-t text-xs text-muted-foreground">
        {drug.due
          ? <><span className="font-medium text-foreground">{drug.due.toLocaleString()}</span> {drug.due === 1 ? 'renewal' : 'renewals'} due in the next 30 days · <span className="font-medium text-foreground">{money(drug.dueAmount)}</span></>
          : 'No renewals due in the next 30 days'}
      </div>
    </div>
  )
}

export default function DrugSupplyRings({ byDrug = [] }) {
  // The All ring: every molecule's lengths summed.
  const all = useMemo(() => {
    const lengths = new Map()
    for (const d of byDrug) {
      for (const i of d.intervals) {
        const a = lengths.get(i.interval) || { ...i, count: 0, mrr: 0, due: 0, dueAmount: 0 }
        a.count += i.count
        a.mrr += i.mrr
        a.due += i.due
        a.dueAmount += i.dueAmount
        lengths.set(i.interval, a)
      }
    }
    return {
      drug: 'All',
      count: byDrug.reduce((s, d) => s + d.count, 0),
      mrr: byDrug.reduce((s, d) => s + d.mrr, 0),
      due: byDrug.reduce((s, d) => s + d.due, 0),
      dueAmount: byDrug.reduce((s, d) => s + d.dueAmount, 0),
      intervals: [...lengths.values()].sort((a, b) => (a.months ?? Infinity) - (b.months ?? Infinity)),
    }
  }, [byDrug])

  if (!all.count) return null

  return (
    <div className="supply-viz">
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
        {all.intervals.map(i => (
          <span key={i.interval} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: colorOf(i.months) }} />
            {i.interval}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {byDrug.length > 1 && <Ring drug={all} />}
        {byDrug.map(d => <Ring key={d.drug} drug={d} share={pct(d.count, all.count)} />)}
      </div>
    </div>
  )
}
